import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingBag,
  Download,
  MessageCircle,
  Heart,
  CheckCircle2,
  Loader2,
  Shield,
  Copy,
  CheckCheck,
  QrCode,
  ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WHATSAPP_AUTHOR_NUMBER = "5581981739909"; // Carlos Medeiros

function formatPhone(v) {
  const digits = v.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

const isEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

export default function PurchaseDialog({ book, open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState("lead"); // lead | pix | confirming | success
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [purchase, setPurchase] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setStep("lead");
    setForm({ name: "", email: "", phone: "" });
    setPurchase(null);
    setCopied(false);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) setTimeout(reset, 300);
    onOpenChange?.(isOpen);
  };

  // Step 1 → create user + purchase
  const submitLead = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error("Informe seu nome.");
      return;
    }
    const hasEmail = isEmail(form.email.trim());
    const hasPhone = form.phone.replace(/\D/g, "").length >= 10;
    if (!hasEmail && !hasPhone) {
      toast.error("Informe um e-mail ou WhatsApp válido.");
      return;
    }
    setStep("loading-lead");
    try {
      // Save lead (best-effort)
      await api.post("/users", {
        name: form.name.trim(),
        email: hasEmail ? form.email.trim() : null,
        phone: hasPhone ? form.phone.replace(/\D/g, "") : null,
      }).catch(() => {});

      const { data } = await api.post("/purchase", {
        book_slug: book.slug,
        name: form.name.trim(),
        email: hasEmail ? form.email.trim() : `${form.phone.replace(/\D/g, "")}@semmail.local`,
        phone: hasPhone ? form.phone.replace(/\D/g, "") : "",
      });
      setPurchase(data);
      setStep("pix");
    } catch {
      toast.error("Não foi possível iniciar a compra. Tente novamente.");
      setStep("lead");
    }
  };

  // Step 2 → user clicked "Já paguei"
  const confirmPayment = async () => {
    if (!purchase?.access_token) return;
    setStep("confirming");
    try {
      const { data } = await api.post(`/purchase/${purchase.access_token}/confirm`);
      setPurchase(data);
      setStep("success");
      onSuccess?.(data);
    } catch {
      toast.error("Ainda não identificamos seu pagamento. Aguarde alguns instantes e tente novamente.");
      setStep("pix");
    }
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(purchase.pix_payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Código PIX copiado!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const whatsappMessage = encodeURIComponent(
    `Olá! Acabei de adquirir "${book?.title}" pelo site. Meu nome é ${form.name || "..."}.`
  );
  const whatsappUrl = `https://wa.me/${WHATSAPP_AUTHOR_NUMBER}?text=${whatsappMessage}`;

  if (!book) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="purchase-dialog"
        className="max-w-xl rounded-none border-[#E5E7EB] p-0 overflow-hidden bg-white max-h-[92vh] overflow-y-auto"
      >
        {/* ============================= STEP 1: LEAD ============================= */}
        {(step === "lead" || step === "loading-lead") && (
          <div className="p-8 md:p-10">
            <DialogHeader className="text-left">
              <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium mb-4">
                Comece sua jornada
              </p>
              <DialogTitle className="font-serif-display text-[1.85rem] md:text-[2.4rem] leading-[1.05] tracking-[-0.022em] text-[#0B1E36] font-medium">
                {book.title}
              </DialogTitle>
              {book.subtitle && (
                <DialogDescription className="font-serif-display italic text-[#0B1E36]/70 text-lg leading-snug mt-3">
                  {book.subtitle}
                </DialogDescription>
              )}
            </DialogHeader>

            <div className="mt-6 flex items-baseline gap-3 pb-6 border-b border-[#E5E7EB]">
              <span className="font-serif-display text-[2rem] text-[#C5A059]">
                {book.price}
              </span>
              <span className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">
                E-book · PDF
              </span>
            </div>

            <div className="mt-6 bg-[#FAFAFA] border border-[#E5E7EB] px-5 py-4">
              <p className="text-sm text-[#2F374A] leading-[1.7] font-light">
                <span className="font-serif-display italic text-[#0B1E36] text-base">
                  Seja muito bem-vindo(a).
                </span>{" "}
                Este espaço foi criado para edificar vidas, fortalecer a fé e
                gerar transformação real.
              </p>
            </div>

            <form onSubmit={submitLead} className="mt-7 space-y-5">
              <div>
                <Label
                  htmlFor="p-name"
                  className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] font-medium"
                >
                  Nome completo
                </Label>
                <Input
                  id="p-name"
                  data-testid="purchase-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Seu nome"
                  className="mt-2 rounded-none h-12 border-[#E5E7EB] bg-white focus-visible:border-[#C5A059] focus-visible:ring-0"
                />
              </div>
              <div>
                <Label
                  htmlFor="p-email"
                  className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] font-medium"
                >
                  E-mail
                </Label>
                <Input
                  id="p-email"
                  type="email"
                  data-testid="purchase-input-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="seu@email.com"
                  className="mt-2 rounded-none h-12 border-[#E5E7EB] bg-white focus-visible:border-[#C5A059] focus-visible:ring-0"
                />
              </div>
              <div>
                <Label
                  htmlFor="p-phone"
                  className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] font-medium"
                >
                  WhatsApp
                </Label>
                <Input
                  id="p-phone"
                  data-testid="purchase-input-phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: formatPhone(e.target.value) })
                  }
                  placeholder="(11) 98765-4321"
                  className="mt-2 rounded-none h-12 border-[#E5E7EB] bg-white focus-visible:border-[#C5A059] focus-visible:ring-0"
                />
                <p className="mt-2 text-xs text-[#6B7280] font-light">
                  Informe pelo menos um dos dois: e-mail ou WhatsApp.
                </p>
              </div>

              <button
                type="submit"
                disabled={step === "loading-lead"}
                data-testid="purchase-submit"
                className="btn-primary-gold w-full mt-2 disabled:opacity-60"
              >
                {step === "loading-lead" ? (
                  <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                ) : (
                  <>
                    Gerar PIX de pagamento
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </>
                )}
              </button>

              <div className="flex items-start gap-2 pt-3">
                <Shield
                  size={13}
                  strokeWidth={1.5}
                  className="text-[#C5A059] mt-0.5 flex-shrink-0"
                />
                <p className="text-xs text-[#6B7280] leading-[1.6] font-light">
                  Seus dados são usados apenas para entrega do e-book e
                  comunicação. O acesso é liberado após confirmação do pagamento.
                </p>
              </div>
            </form>
          </div>
        )}

        {/* ============================= STEP 2: PIX ============================== */}
        {step === "pix" && purchase && (
          <div data-testid="purchase-pix" className="p-8 md:p-10">
            <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium mb-3">
              Pagamento via PIX
            </p>
            <h3 className="font-serif-display text-[1.65rem] md:text-[2rem] leading-[1.1] tracking-[-0.02em] text-[#0B1E36] font-medium">
              Realize o pagamento para liberar seu acesso.
            </h3>
            <p className="mt-4 text-sm text-[#6B7280] leading-[1.7] font-light">
              Escaneie o QR Code abaixo com o app do seu banco ou copie o código
              PIX e cole no campo de transferência.
            </p>

            <div className="mt-8 grid sm:grid-cols-[auto,1fr] gap-6 items-start bg-[#FAFAFA] border border-[#E5E7EB] p-6">
              <div className="flex justify-center">
                <img
                  src={purchase.pix_qr_data_url}
                  alt="QR Code PIX"
                  data-testid="pix-qr"
                  className="w-44 h-44 bg-white p-2 border border-[#E5E7EB]"
                />
              </div>
              <div>
                <p className="text-[0.68rem] uppercase tracking-[0.28em] text-[#6B7280] font-medium">
                  Valor
                </p>
                <p className="mt-1 font-serif-display text-[2rem] text-[#C5A059] leading-none">
                  R$ {purchase.amount_brl.toFixed(2).replace(".", ",")}
                </p>
                <p className="mt-4 text-[0.68rem] uppercase tracking-[0.28em] text-[#6B7280] font-medium">
                  Chave · {purchase.book_title}
                </p>
                <p className="mt-1 text-sm text-[#0B1E36] break-all">
                  {purchase.book_price}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <Label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] font-medium">
                Código PIX (copia e cola)
              </Label>
              <div className="mt-2 flex gap-2">
                <Input
                  readOnly
                  value={purchase.pix_payload}
                  data-testid="pix-copy-paste"
                  className="rounded-none h-12 border-[#E5E7EB] bg-white font-mono text-xs text-[#0B1E36]"
                />
                <button
                  onClick={copyPix}
                  data-testid="pix-copy-button"
                  className="h-12 px-4 bg-[#0B1E36] text-white flex items-center gap-2 text-xs uppercase tracking-[0.2em] hover:bg-[#13294B] transition-colors duration-300"
                >
                  {copied ? (
                    <CheckCheck size={14} strokeWidth={1.5} />
                  ) : (
                    <Copy size={14} strokeWidth={1.5} />
                  )}
                  {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>

            <button
              onClick={confirmPayment}
              data-testid="purchase-confirm-payment"
              className="btn-primary-gold w-full mt-8"
            >
              <QrCode size={16} strokeWidth={1.5} />
              Já realizei o pagamento
            </button>

            <div className="mt-6 pt-5 border-t border-[#E5E7EB] flex items-start gap-3">
              <Shield
                size={14}
                strokeWidth={1.5}
                className="text-[#C5A059] mt-0.5 flex-shrink-0"
              />
              <p className="text-xs text-[#6B7280] leading-[1.7] font-light">
                Pagamento seguro. QR Code gerado pelo servidor. Após a
                confirmação, liberamos o download e enviamos uma cópia do e-book
                para você.
              </p>
            </div>
          </div>
        )}

        {/* ============================ STEP 3: CONFIRMING ======================== */}
        {step === "confirming" && (
          <div className="p-16 text-center">
            <Loader2
              size={32}
              strokeWidth={1.5}
              className="text-[#C5A059] mx-auto animate-spin"
            />
            <p className="mt-6 font-serif-display text-2xl text-[#0B1E36] italic">
              Confirmando seu pagamento...
            </p>
            <p className="mt-2 text-sm text-[#6B7280]">
              Preparando a entrega do seu exemplar.
            </p>
          </div>
        )}

        {/* ============================ STEP 4: SUCCESS =========================== */}
        {step === "success" && purchase && (
          <div data-testid="purchase-success">
            <div className="bg-[#0B1E36] text-white p-10 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-noise" aria-hidden />
              <div
                className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(197,160,89,0.2) 0%, transparent 70%)",
                }}
                aria-hidden
              />
              <div className="relative">
                <CheckCircle2
                  size={40}
                  strokeWidth={1.5}
                  className="text-[#C5A059] mx-auto"
                />
                <p className="mt-6 text-[0.68rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                  Pagamento confirmado
                </p>
                <h3 className="mt-4 font-serif-display text-[2rem] md:text-[2.5rem] leading-[1.08] tracking-[-0.022em] font-medium">
                  Seu e-book está disponível.
                </h3>
                <p className="mt-4 font-serif-display italic text-lg text-white/75 leading-relaxed">
                  Que esse conteúdo transforme sua vida.
                </p>
              </div>
            </div>

            <div className="p-8 md:p-10 bg-white">
              <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                Você acaba de fazer parte de algo maior
              </p>
              <p className="mt-4 text-[1rem] md:text-[1.05rem] text-[#2F374A] leading-[1.82] font-light">
                Cada livro adquirido contribui diretamente para o cuidado de
                crianças e famílias atendidas pelo{" "}
                <span className="text-[#0B1E36] font-medium">
                  Instituto Garoto da Arena
                </span>
                . Obrigado por fazer parte dessa missão.
              </p>
              <p className="mt-6 font-serif-display italic text-[#0B1E36] text-right text-base">
               , Carlos Eduardo Medeiros
              </p>

              <div className="mt-8 space-y-3">
                {purchase.pdf_url ? (
                  <a
                    href={purchase.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="purchase-download"
                    className="btn-primary-gold w-full"
                  >
                    <Download size={16} strokeWidth={1.5} />
                    Baixar e-book agora
                  </a>
                ) : (
                  <div className="bg-[#FAFAFA] border border-[#E5E7EB] px-5 py-4 text-sm text-[#6B7280] leading-relaxed">
                    Este título será enviado por e-mail em instantes. Confira
                    também sua caixa de spam.
                  </div>
                )}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="purchase-whatsapp"
                  className="btn-secondary-dark w-full"
                >
                  <MessageCircle size={16} strokeWidth={1.5} />
                  Conversar no WhatsApp
                </a>
              </div>

              <div className="mt-8 pt-6 border-t border-[#E5E7EB] flex items-start gap-3">
                <Heart
                  size={14}
                  strokeWidth={1.5}
                  className="text-[#C5A059] mt-0.5 flex-shrink-0"
                />
                <p className="text-xs text-[#6B7280] leading-[1.7] font-light">
                  Enviamos uma cópia também para{" "}
                  <span className="text-[#0B1E36] font-medium">
                    {purchase.email}
                  </span>
                  . Verifique sua caixa nos próximos minutos.
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
